const DEFAULT_MESSAGE = "Something went wrong";

// Keys the standard envelope itself uses. Skipped when scanning for a field error, or a body
// like { code: "internal_error" } would surface its own code as the user-facing message.
const ENVELOPE_KEYS = new Set(["detail", "code", "field", "trace", "warnings", "overpayment"]);

// Some views return DRF's `serializer.errors` straight out instead of raising, so the response
// never passes through the backend's error formatter and arrives as { field: ["message"] } with
// no `detail`. Read the first message out of it rather than falling back to the generic default.
const fieldError = (data) => {
  for (const [key, value] of Object.entries(data)) {
    if (ENVELOPE_KEYS.has(key)) {
      continue;
    }
    const first = Array.isArray(value) ? value[0] : value;
    if (typeof first === "string" && first) {
      // `non_field_errors` belongs to the form as a whole, so there is no input to point at.
      return { message: first, field: key === "non_field_errors" ? undefined : key };
    }
  }
  return null;
};

const isArrayBuffer = (value) =>
  value != null &&
  (value instanceof ArrayBuffer ||
    Object.prototype.toString.call(value) === "[object ArrayBuffer]");

const decodeArrayBuffer = (buffer) => {
  try {
    const decoded = new TextDecoder().decode(buffer);
    return decoded ? JSON.parse(decoded) : null;
  } catch {
    return null;
  }
};

export const parseApiError = (error) => {
  if (!error) {
    return { message: DEFAULT_MESSAGE };
  }

  const response = error.response;

  if (!response) {
    return { message: error.message || DEFAULT_MESSAGE };
  }

  let data = response.data;

  if (isArrayBuffer(data)) {
    data = decodeArrayBuffer(data);
  }

  if (data && typeof data === "object") {
    const fallback = data.detail == null ? fieldError(data) : null;
    const message =
      typeof data.detail === "string" && data.detail
        ? data.detail
        : data.detail != null
          ? String(data.detail)
          : (fallback?.message ?? DEFAULT_MESSAGE);

    return {
      message,
      status: response.status,
      code: typeof data.code === "string" ? data.code : undefined,
      raw: data,
      // Names the input a single-field error belongs to. The backend envelope sends this itself;
      // here it is recovered from the raw field dict so callers read the same property either way.
      ...(typeof data.field === "string" && data.field
        ? { field: data.field }
        : fallback?.field
          ? { field: fallback.field }
          : {}),
      // Some services (e.g. html-to-pdf) attach correlation IDs under `trace`
      // (logrocket_url / trace_id / otel_trace_id) so a failure can be tied to a
      // replay or distributed trace. Surface it when present; omit otherwise so
      // the standard { detail, code } shape is unchanged.
      ...(data.trace && typeof data.trace === "object" ? { trace: data.trace } : {}),
      // User-actionable accounting warnings: [{ detail, code, action: { label, href } }].
      // The UI renders each action as a button instead of receiving HTML in `detail`.
      // Present only when code === "accounting_action_required".
      ...(Array.isArray(data.warnings) ? { warnings: data.warnings } : {}),
    };
  }

  if (typeof data === "string" && data) {
    return { message: data, status: response.status, raw: data };
  }

  return { message: error.message || DEFAULT_MESSAGE, status: response.status };
};

export default parseApiError;
